import React, { forwardRef, useImperativeHandle, useState } from 'react'
import cn from 'classnames'

import styles from './VNNotesField.module.scss'

const NOTES_CHARACTER_LIMIT = 255

/**
 * A VN Input Field that allows notes to be set and retrieved with the option to pass in an on blur event.
 * @param {string} previousNotes - Notes that were previously saved and attached to an entity (Item, Object, etc) to be used as the default
 * @param {string} label - Placeholder text for the input field
 * @param {func} onBlur - Optional method to be called when the input field is blurred
 * @param {string} className - Optional className to provide additional styling if desired
 * @param {object} ref - passed in ref that exposes the note state to the parent component through getNotes
 */

const VNNotesField = forwardRef(({ previousNotes, label = 'Add a note', onBlur = (() => {}), className }, ref) => {
  const [notes, setNotes] = useState(previousNotes ?? '')
  const characterCount = notes.length
  const classNames = cn(className, styles.notesContainer, { [styles.emptyNotes]: characterCount === 0 })

  useImperativeHandle(ref, () => ({
    // Gets the current value of notes
    getNotes: () => notes,
  }));

  // Trims and sets the notes on change of the input
  const handleSetNotes = (event) => {
    const notes = event?.target?.value ?? ''
    const trimmedNotes = notes.substring(0, NOTES_CHARACTER_LIMIT)
    setNotes(trimmedNotes)
  }

  return (
    <div className={classNames}>
      <textarea
        placeholder={label}
        value={notes}
        onChange={handleSetNotes}
        className={styles.notes}
        onBlur={() => onBlur(notes)}
      />
      <span className={styles.characterCount}>{`${characterCount}/${NOTES_CHARACTER_LIMIT}`}</span>
    </div>
  )
})

export default VNNotesField
